import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { listTemplates, type TemplateMeta } from "@/lib/template-queries";
import { fetchActiveSeasonal } from "@/lib/seasonal-queries";
import { getTemplateUsageCounts, type TemplateUsageCount } from "@/lib/api/template-usage.functions";
import { TemplateUsageCardBadge } from "@/components/templates/template-usage-badge";
import { SiteHeader } from "@/components/site-header";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { hreflangLinks } from "@/lib/hreflang";

const FILTER_STORAGE_KEY = "checkbloom.templates.filter.v1";

const LOCALE_CATEGORIES = [
  "Deutsches Leben",
  "Nederlands Leven",
  "Nordic Life Admin",
  "UK Life Admin",
  "Vida Española",
  "Vie Française",
];

type Filter = "all" | "free" | "premium";

type PersistedFilters = {
  search: string;
  filter: Filter;
  activeCat: string;
};

type TemplatesSearch = {
  seasonal?: string;
  locale?: string;
  drawer?: number;
};

export const Route = createFileRoute("/templates")({
  validateSearch: (s: Record<string, unknown>): TemplatesSearch => ({
    seasonal: typeof s.seasonal === "string" ? s.seasonal : undefined,
    locale: typeof s.locale === "string" ? s.locale : undefined,
    drawer: typeof s.drawer === "number" ? s.drawer : typeof s.drawer === "string" ? Number(s.drawer) || undefined : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Checkbloom Templates — 535+ checklists for life's moments" },
      {
        name: "description",
        content:
          "Browse 535+ pre-built household checklists — moving, baby, hosting, home maintenance, and more. Free to preview, free to use.",
      },
      { property: "og:title", content: "Checkbloom Templates" },
      {
        property: "og:description",
        content: "535+ checklists for every moment life throws at you.",
      },
      { property: "og:url", content: "https://checkbloom.app/templates" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/f90e42af-1eda-4a89-bf90-d40a17f2cb98" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/f90e42af-1eda-4a89-bf90-d40a17f2cb98" },
    ],
    links: [{ rel: "canonical", href: "https://checkbloom.app/templates" }, ...hreflangLinks("https://checkbloom.app/templates")],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Checkbloom Templates",
          description:
            "535+ pre-built household checklists for life's biggest moments.",
          url: "https://checkbloom.app/templates",
        }),
      },
    ],
  }),
  errorComponent: ({ error, reset }) => (
    <div className="p-8 text-center">
      <p className="text-sm text-muted-foreground">Couldn't load templates.</p>
      <button className="mt-3 text-sm text-primary underline" onClick={reset}>
        Retry
      </button>
      <p className="mt-2 text-xs text-destructive">{error instanceof Error ? error.message : String(error)}</p>
    </div>
  ),
  notFoundComponent: () => <div className="p-8">Not found.</div>,
  component: TemplatesLayout,
});

// Lightweight fuzzy: case-insensitive, all search tokens must appear in
// title+category+description.
function matchesFuzzy(t: TemplateMeta, needle: string): boolean {
  if (!needle) return true;
  const hay = `${t.title} ${t.category} ${t.description}`.toLowerCase();
  return needle
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((tok) => hay.includes(tok));
}

function loadPersisted(): PersistedFilters | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function TemplatesLayout() {
  // When a child template route is matched (/templates/$id), render only the
  // child so the standalone page is the entire page. Drawer usage passes
  // ?drawer=1 and is rendered by the child; we still show the listing behind
  // it in that mode.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const drawerMode = useRouterState({
    select: (s) => Boolean((s.location.search as { drawer?: number }).drawer),
  });
  // If the visitor came from a localized templates page (de/fr/nl/es), we
  // must NOT render the English listing behind the drawer — it leaks through
  // the semi-transparent backdrop and closing would visually dump them into
  // English. In that case, render only the child (drawer) via <Outlet />.
  const [hasLocaleContext, setHasLocaleContext] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    setHasLocaleContext(!!sessionStorage.getItem("checkbloom_locale"));
  }, [drawerMode, pathname]);
  const isAtListing =
    pathname === "/templates" ||
    pathname === "/templates/" ||
    (drawerMode && !hasLocaleContext);

  const q = useQuery({ queryKey: ["templates", "list"], queryFn: listTemplates });
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [activeCat, setActiveCat] = useState<string>("All");
  const [userId, setUserId] = useState<string | null>(null);
  const [householdSituation, setHouseholdSituation] = useState<string | null>(null);
  const [hemisphere, setHemisphere] = useState<string | null>(null);
  const navigate = useNavigate();
  const { seasonal: seasonalParam } = Route.useSearch();

  // Restore persisted filters on mount
  useEffect(() => {
    const p = loadPersisted();
    if (p) {
      setSearch(p.search ?? "");
      setFilter(p.filter ?? "all");
      setActiveCat(p.activeCat ?? "All");
    }
  }, []);

  // Persist on change (search is transient — don't persist it across sessions)
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(
        FILTER_STORAGE_KEY,
        JSON.stringify({ search: "", filter, activeCat }),
      );
    } catch {
      /* ignore */
    }
  }, [filter, activeCat]);


  // Look up current user + household situation/hemisphere (optional)
  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return;
      setUserId(u.user.id);
      const { data: hm } = await supabase
        .from("household_members")
        .select("household_id")
        .eq("user_id", u.user.id)
        .order("joined_at", { ascending: true })
        .limit(1)
        .maybeSingle();
      if (!hm?.household_id) return;
      const { data: hh } = await supabase
        .from("households")
        .select("situation,hemisphere")
        .eq("id", hm.household_id)
        .maybeSingle();
      setHouseholdSituation(hh?.situation ?? null);
      setHemisphere(hh?.hemisphere ?? null);
    })();
  }, []);

  // Active seasonal moment (for the featured banner at top)
  const seasonalQ = useQuery({
    queryKey: ["seasonal", "active", hemisphere ?? "northern"],
    queryFn: () => fetchActiveSeasonal(hemisphere),
  });
  const activeSeasonal = seasonalQ.data ?? null;

  const all = q.data ?? [];

  // Batched usage counts for every loaded template (single request).
  const fetchUsage = useServerFn(getTemplateUsageCounts);
  const allIds = useMemo(() => all.map((t) => t.id).sort(), [all]);
  const usageQ = useQuery({
    queryKey: ["templates", "usage-counts", allIds],
    queryFn: () => fetchUsage({ data: { templateIds: allIds } }),
    enabled: allIds.length > 0,
    staleTime: 60 * 60_000, // 1 hour
    gcTime: 60 * 60_000,
  });
  const usageById = useMemo(() => {
    const m = new Map<string, TemplateUsageCount>();
    (usageQ.data ?? []).forEach((r) => m.set(r.template_id, r));
    return m;
  }, [usageQ.data]);
  const categories = useMemo(() => {
    const s = new Set<string>();
    all.forEach((t) => {
      if (!LOCALE_CATEGORIES.includes(t.category)) s.add(t.category);
    });
    return ["All", ...Array.from(s).sort()];
  }, [all]);

  const filtered = useMemo(() => {
    return all.filter((t) => {
      if (activeCat !== "All" && t.category !== activeCat) return false;
      if (filter === "free" && t.is_premium) return false;
      if (filter === "premium" && !t.is_premium) return false;
      return matchesFuzzy(t, search.trim());
    });
  }, [all, activeCat, filter, search]);

  // If navigated via ?seasonal=<id>, focus that moment's templates.
  const focusSeasonal = useMemo(() => {
    if (!seasonalParam || !activeSeasonal) return null;
    if (activeSeasonal.id !== seasonalParam) return null;
    const ids = new Set(activeSeasonal.template_ids ?? []);
    return all.filter((t) => ids.has(t.id));
  }, [seasonalParam, activeSeasonal, all]);

  // Recommended for you: simple match against household.situation tokens; fall
  // back to a handful of broadly useful templates.
  const recommended = useMemo<TemplateMeta[]>(() => {
    if (!userId || !all.length) return [];
    const sit = (householdSituation ?? "").toLowerCase();
    let pool: TemplateMeta[] = [];
    if (sit) {
      const tokens = sit.split(/[\s,_-]+/).filter((t) => t.length > 2);
      pool = all.filter((t) => {
        const hay = `${t.title} ${t.category} ${t.description}`.toLowerCase();
        return tokens.some((tok) => hay.includes(tok));
      });
    }
    if (pool.length < 4) {
      // Fallback: most-used (community_count) free templates not already in pool.
      const seen = new Set(pool.map((p) => p.id));
      const extras = [...all]
        .filter((t) => !t.is_premium && !seen.has(t.id))
        .sort((a, b) => b.community_count - a.community_count)
        .slice(0, 4 - pool.length);
      pool = [...pool, ...extras];
    }
    return pool.slice(0, 4);
  }, [userId, householdSituation, all]);

  const grouped = useMemo(() => {
    const m = new Map<string, TemplateMeta[]>();
    filtered.forEach((t) => {
      if (!m.has(t.category)) m.set(t.category, []);
      m.get(t.category)!.push(t);
    });
    return Array.from(m.entries());
  }, [filtered]);

  if (!isAtListing) {
    return (
      <>
        <SiteHeader />
        <Outlet />
      </>
    );
  }


  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      {/* Featured seasonal banner — full-width, not dismissible here */}
      {activeSeasonal && (
        <div className="border-b border-primary/20 bg-primary/5">
          <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-4">
            <span className="text-3xl">{activeSeasonal.emoji ?? "🌿"}</span>
            <div className="flex-1">
              <h2 className="font-display text-base text-foreground">
                {activeSeasonal.title}
              </h2>
              {activeSeasonal.subtitle && (
                <p className="text-[13px] text-muted-foreground">
                  {activeSeasonal.subtitle}
                </p>
              )}
            </div>
            <span className="text-xs text-muted-foreground">
              {activeSeasonal.template_ids?.length ?? 0} templates
            </span>
          </div>
        </div>
      )}

      <header className="border-b border-border/60 bg-cream/60 backdrop-blur">
        <div className="mx-auto max-w-6xl px-4 py-10 md:py-14">
          <Link to="/" className="text-xs uppercase tracking-widest text-muted-foreground">
            Checkbloom
          </Link>
          <h1 className="mt-2 font-display text-3xl md:text-4xl text-foreground">
            535+ checklists for every moment life throws at you.
          </h1>
          <p className="mt-3 text-muted-foreground">Tap any to preview.</p>

          <div className="mt-6 flex flex-col gap-3 md:flex-row md:items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search templates..."
                className="pl-9"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Clear"
                >
                  <X className="size-4" />
                </button>
              )}
            </div>
            <div className="flex gap-2">
              {(["all", "free", "premium"] as Filter[]).map((f) => (
                <button
                  key={f}
                  onClick={() => {
                    if (f === "all") {
                      setFilter("all");
                      setActiveCat("All");
                    } else {
                      setFilter(f);
                    }
                  }}
                  className={`rounded-full border px-3 py-1 text-xs capitalize transition ${
                    filter === f
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2 overflow-x-auto">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActiveCat(c)}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition ${
                  activeCat === c
                    ? "bg-foreground text-background"
                    : "bg-muted/60 text-muted-foreground hover:bg-muted"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8">
        {/* Focus mode when arriving from dashboard seasonal banner */}
        {focusSeasonal && focusSeasonal.length > 0 && !search.trim() && activeCat === "All" && filter === "all" && (
          <section className="mb-10">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-display text-lg text-foreground">
                {activeSeasonal?.emoji} {activeSeasonal?.title}
              </h2>
              <button
                onClick={() => navigate({ to: "/templates", search: {} })}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Show all
              </button>
            </div>
            <TemplateGrid usageById={usageById} list={focusSeasonal} onPick={(id) => navigate({ to: "/templates/$id", params: { id }, search: { drawer: 1 } })} />
          </section>
        )}

        {/* Recommended for you (signed-in only) */}
        {!focusSeasonal && recommended.length > 0 && !search.trim() && activeCat === "All" && filter === "all" && (
          <section className="mb-10">
            <h2 className="mb-3 font-display text-lg text-foreground">Recommended for you</h2>
            <TemplateGrid usageById={usageById} list={recommended} onPick={(id) => navigate({ to: "/templates/$id", params: { id }, search: { drawer: 1 } })} />
          </section>
        )}

        {q.isLoading ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="h-28 animate-pulse rounded-2xl bg-muted/50" />
            ))}
          </div>
        ) : grouped.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              No templates match. Try a different search or filter.
            </p>
            <button
              onClick={() => {
                setSearch("");
                setFilter("all");
                setActiveCat("All");
              }}
              className="mt-3 text-sm text-primary underline"
            >
              Clear filters
            </button>
          </div>

        ) : (
          grouped.map(([cat, list]) => (
            <section key={cat} className="mb-10">
              <h2 className="mb-3 font-display text-lg text-foreground">{cat}</h2>
              <TemplateGrid usageById={usageById} list={list} onPick={(id) => navigate({ to: "/templates/$id", params: { id }, search: { drawer: 1 } })} />
            </section>
          ))
        )}
      </main>

      <Outlet />
    </div>
  );
}

function TemplateGrid({
  list,
  onPick,
  usageById,
}: {
  list: TemplateMeta[];
  onPick: (id: string) => void;
  usageById?: Map<string, TemplateUsageCount>;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {list.map((t) => (
        <button
          key={t.id}
          onClick={() => {
            sessionStorage.removeItem('checkbloom_locale');
            onPick(t.id);
          }}
          className="group flex items-start gap-3 rounded-2xl border border-border bg-card p-4 text-left transition hover:border-primary/40 hover:shadow-sm"
        >
          <span className="text-2xl">{t.emoji_icon}</span>
          <div className="flex-1">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-display text-base text-foreground">{t.title}</h3>
              <Badge
                variant={t.is_premium ? "default" : "secondary"}
                className={
                  t.is_premium
                    ? "bg-accent text-accent-foreground"
                    : "bg-muted text-muted-foreground"
                }
              >
                {t.is_premium ? "Premium" : "Free"}
              </Badge>
            </div>
            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{t.description}</p>
            <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
              <span>{t.item_count} items</span>
            </div>
            <TemplateUsageCardBadge
              counts={usageById?.get(t.id) ?? null}
              isSensitive={t.is_sensitive}
              className="mt-2"
            />
          </div>
        </button>
      ))}
    </div>
  );
}
